import { Dialog, useIsCompact } from "@dl/web-core-js/ui";
import { Link } from "@tanstack/react-router";
import { ExternalLink, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { MaterialStory } from "./MaterialStory";

/**
 * Why a material's quantity is what it is, beside the list rather than on top
 * of it: the buyer keeps scrolling and pressing i on the next row. On a
 * tablet there is no room beside, so it opens as a dialog.
 */
export function ExplainPanel({ partId, warehouseId = null, onClose }: { partId: number | null; warehouseId?: number | null; onClose: () => void }) {
  const { t } = useTranslation();
  const compact = useIsCompact();
  if (partId === null) return null;

  const title = t("replenishment.explain.title", "Why this quantity");
  if (compact) {
    return (
      <Dialog open onClose={onClose} title={title} size="lg">
        <MaterialStory partId={partId} warehouseId={warehouseId} compact />
      </Dialog>
    );
  }
  return (
    <aside className="flex w-[28rem] shrink-0 flex-col border-l border-line bg-surface xl:w-[32rem]" aria-label={title}>
      <div className="flex items-center justify-between gap-2 border-b border-line px-4 py-2.5">
        <h2 className="text-[13px] font-semibold text-ink">{title}</h2>
        <div className="flex items-center gap-1">
          <Link
            to="/manufacturing/parts/$partId"
            params={{ partId: String(partId) }}
            className="flex size-8 items-center justify-center rounded-md text-ink-muted hover:bg-surface-sunk hover:text-ink"
            title={t("replenishment.explain.open-part", "Open the part")}
          >
            <ExternalLink className="size-4" />
          </Link>
          <button
            type="button"
            onClick={onClose}
            className="flex size-8 items-center justify-center rounded-md text-ink-muted hover:bg-surface-sunk hover:text-ink"
            aria-label={t("dialog.close", "Close")}
            title="Esc"
          >
            <X className="size-4" />
          </button>
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-auto p-4">
        <MaterialStory partId={partId} warehouseId={warehouseId} compact />
      </div>
    </aside>
  );
}
